/**
 * Client HTTP typé.
 *
 * `fetch` enveloppé, pas d'axios : le besoin tient en cent lignes, et une
 * dépendance de plus se paie à chaque montée de version.
 */

import { config } from '../config'
import { ApiError, ErreurReseau } from './erreurs'
import {
  lireAccessToken,
  lireRefreshToken,
  poserAccessToken,
  poserRefreshToken,
  purgerJetons,
} from './jetons'
import type { ReponseJetons } from './types'

/**
 * URL absolue d'un chemin d'API.
 *
 * `apiBaseUrl` vaut `/api/v1` : une URL relative, que le navigateur résout tout
 * seul contre l'origine courante. Le `fetch` de Node, lui, la refuse — il exige
 * une URL absolue. Sans cette résolution, tout marche dans le navigateur et
 * rien ne marche en test, pour une raison qui n'a aucun rapport avec le code
 * testé.
 */
function urlComplete(chemin: string): string {
  const base = config.apiBaseUrl
  if (/^https?:\/\//i.test(base)) return `${base}${chemin}`
  const origine = typeof window === 'undefined' ? '' : window.location.origin
  return `${origine}${base}${chemin}`
}

interface Options {
  methode?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  corps?: unknown
  /** Usage interne : empêche qu'une requête soit rejouée deux fois. */
  dejaRejouee?: boolean
  signal?: AbortSignal
}

/** Appelé quand la session devient irrécupérable. Branché par l'application. */
let surSessionPerdue: (() => void) | null = null

export function definirSurSessionPerdue(rappel: () => void): void {
  surSessionPerdue = rappel
}

function perdreLaSession(): void {
  purgerJetons()
  surSessionPerdue?.()
}

/* ------------------------------------------------------------------------- */
/* Rafraîchissement : une seule promesse partagée                            */
/* ------------------------------------------------------------------------- */

/**
 * ============================================================================
 * LE PIÈGE, ET POURQUOI CE CODE EST ÉCRIT AINSI.
 *
 * Cinq requêtes partent en parallèle et prennent toutes un 401. Sans
 * précaution, chacune lance son propre rafraîchissement. Or le refresh token
 * est **à usage unique et rotatif** (§6) : le premier appel le consomme, les
 * quatre suivants présentent un jeton déjà révoqué — et le backend, qui traite
 * cela comme le signe d'une fuite, révoque *toute la famille*. L'utilisateur
 * est déconnecté sans raison apparente, et le bug est quasi impossible à
 * reproduire à la main.
 *
 * Trois règles le cassent, et elles doivent tenir ensemble :
 *   1. `/auth/rafraichir` est exclue de l'intercepteur. Un 401 dessus, c'est
 *      purge et redirection, point final — sinon on boucle.
 *   2. Une seule promesse partagée : les requêtes en 401 l'attendent.
 *   3. Un seul rejeu par requête (`dejaRejouee`), sinon la boucle revient par
 *      la fenêtre.
 * ============================================================================
 */
let rafraichissementEnCours: Promise<void> | null = null

async function executerRafraichissement(): Promise<void> {
  const refresh = lireRefreshToken()
  if (!refresh) {
    perdreLaSession()
    throw new ApiError(401, { detail: 'Session expirée.', code: 'JETON_INVALIDE' })
  }

  const reponse = await fetch(urlComplete('/auth/rafraichir'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh }),
  })

  if (!reponse.ok) {
    // Règle 1 : aucun second rafraîchissement ici, quoi qu'il arrive.
    perdreLaSession()
    throw new ApiError(401, { detail: 'Session expirée.', code: 'JETON_INVALIDE' })
  }

  const corps = (await reponse.json()) as ReponseJetons
  poserAccessToken(corps.access_token)
  poserRefreshToken(corps.refresh_token)
}

async function rafraichirUneSeuleFois(): Promise<void> {
  rafraichissementEnCours ??= executerRafraichissement().finally(() => {
    rafraichissementEnCours = null
  })
  return rafraichissementEnCours
}

/** Réservé aux tests : oublie le rafraîchissement en vol. */
export function reinitialiserRafraichissement(): void {
  rafraichissementEnCours = null
}

/* ------------------------------------------------------------------------- */
/* Requête                                                                    */
/* ------------------------------------------------------------------------- */

async function lireErreur(reponse: Response): Promise<ApiError> {
  try {
    const corps = (await reponse.json()) as Record<string, unknown>
    return new ApiError(reponse.status, {
      detail: typeof corps.detail === 'string' ? corps.detail : undefined,
      code: typeof corps.code === 'string' ? corps.code : undefined,
      champ: typeof corps.champ === 'string' ? corps.champ : null,
    })
  } catch {
    // 500 sans corps JSON, page d'erreur du mandataire, réponse tronquée…
    return new ApiError(reponse.status, {
      detail: "Le serveur a renvoyé une réponse inattendue.",
      code: 'REPONSE_ILLISIBLE',
    })
  }
}

/**
 * Une requête annulée n'est pas une panne : elle remonte telle quelle pour que
 * l'appelant l'ignore.
 *
 * On ne teste pas `instanceof DOMException` : selon l'environnement,
 * l'exception provient d'un autre contexte d'exécution et le test échoue
 * silencieusement — la requête annulée s'affiche alors comme « serveur
 * injoignable ».
 */
function estAnnulation(cause: unknown): boolean {
  return (cause as { name?: string } | null)?.name === 'AbortError'
}

/**
 * jsdom fournit son propre `AbortController`, que le `fetch` de Node refuse :
 * « Expected signal to be an instance of AbortSignal ».
 *
 * C'est un artefact d'environnement de test, jamais un cas navigateur. Le
 * détecter permet de rejouer sans le signal plutôt que d'annoncer une panne
 * réseau qui n'existe pas — et surtout, d'éviter qu'une erreur de programmation
 * se déguise en incident d'exploitation.
 */
function estSignalEtranger(cause: unknown): boolean {
  const erreur = cause as { message?: string; cause?: { message?: string } } | null
  return `${erreur?.message ?? ''} ${erreur?.cause?.message ?? ''}`.includes('AbortSignal')
}

async function executerFetch(
  url: string,
  init: RequestInit,
  signal?: AbortSignal,
): Promise<Response> {
  try {
    return await fetch(url, { ...init, signal })
  } catch (cause) {
    if (estAnnulation(cause) || signal?.aborted) throw cause
    if (signal && estSignalEtranger(cause)) {
      try {
        return await fetch(url, init)
      } catch (secondeCause) {
        throw new ErreurReseau(secondeCause)
      }
    }
    throw new ErreurReseau(cause)
  }
}

async function requete<T>(chemin: string, options: Options = {}): Promise<T> {
  const { methode = 'GET', corps, dejaRejouee = false, signal } = options

  const entetes: Record<string, string> = {}
  if (corps !== undefined) entetes['Content-Type'] = 'application/json'
  const jeton = lireAccessToken()
  if (jeton) entetes.Authorization = `Bearer ${jeton}`

  const reponse = await executerFetch(
    urlComplete(chemin),
    {
      method: methode,
      headers: entetes,
      body: corps === undefined ? undefined : JSON.stringify(corps),
    },
    signal,
  )

  if (reponse.status === 401 && !dejaRejouee && !chemin.startsWith('/auth/rafraichir')) {
    await rafraichirUneSeuleFois()
    // Règle 3 : ce rejeu est le seul autorisé pour cette requête.
    return requete<T>(chemin, { ...options, dejaRejouee: true })
  }

  if (!reponse.ok) throw await lireErreur(reponse)

  if (reponse.status === 204) return undefined as T
  const texte = await reponse.text()
  if (!texte) return undefined as T
  return JSON.parse(texte) as T
}

/* ------------------------------------------------------------------------- */
/* Surface publique                                                           */
/* ------------------------------------------------------------------------- */

export const api = {
  get: <T>(chemin: string, signal?: AbortSignal) => requete<T>(chemin, { signal }),
  post: <T>(chemin: string, corps?: unknown) => requete<T>(chemin, { methode: 'POST', corps }),
  patch: <T>(chemin: string, corps?: unknown) => requete<T>(chemin, { methode: 'PATCH', corps }),
  delete: <T>(chemin: string) => requete<T>(chemin, { methode: 'DELETE' }),

  /**
   * Téléchargement binaire (PDF, CSV).
   *
   * Le nom du fichier vient de `Content-Disposition` : le §6 le fixe côté
   * serveur (`REL-2026-0001.pdf`), et le réécrire côté client produirait des
   * fichiers dont le nom ne correspond plus au document.
   */
  async telecharger(chemin: string): Promise<{ blob: Blob; nomFichier: string | null }> {
    const entetes: Record<string, string> = {}
    const jeton = lireAccessToken()
    if (jeton) entetes.Authorization = `Bearer ${jeton}`

    const reponse = await fetch(urlComplete(chemin), { headers: entetes })
    if (!reponse.ok) throw await lireErreur(reponse)

    const disposition = reponse.headers.get('Content-Disposition') ?? ''
    const correspondance = /filename="?([^"';]+)"?/i.exec(disposition)
    return { blob: await reponse.blob(), nomFichier: correspondance?.[1] ?? null }
  },
}
